import React, { Component, useEffect } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { Col } from 'react-grid-system'
import classNames from 'classnames';

import {
  decodeJwt,
  UniConditionalRender,
  UserC,
  UniImg,
  UniLoader,
  UniLocalize,
  EMessageType,
  UniMessage,
  UniIcon,
  IAppleWalletPassStatusResponseC, 
} from '@unikey/unikey-commons/release/comm';

import {
  useAppSelector,
  useAppDispatch,
  addAlert,
  IPartnerCustomizations,
  attemptProvisionAppleWalletPasses,  
  PartnerCustomizations,
  appInsights,
  attemptSilentCheckAppleWalletInvitationPassStatus,
  attemptCheckAppleWalletInvitationPassStatus,
} from '../internal'

import {
  appleWalletDomain,
  appleWalletPartnerId,
  appleWalletSandboxDomain
} from '@alias-environment';

const initAppleWalletScript = (
  dispatch: any,
  domainForAppleWallet: string,
  inviteToken: string,
  desiredDealerId?: string,
  desiredDealerName?: string,
  desiredOrgName?: string,
  currentUser?: UserC,
  afterSuccess?: () => void,
) => {
  // this comes from script loaded on the page provided by apple
  // sets up a listener for when the 'add-to-apple-wallet' button is clicked
  /* tslint:disable-next-line */
  initAddToAppleWallet?.({
    domain: domainForAppleWallet,
    cardType: 'ACCESS_CORPORATE',
    partnerId: appleWalletPartnerId,
    buttonId: addToWalletButtonId,
    jwtResolver: () => {
      return dispatch(attemptProvisionAppleWalletPasses({ inviteToken }));
    },
    resultResolver: (result: any) => {
      if (result.status === '200' || result.status === '201') {
        dispatch(addAlert({
          id: Date.now(),
          titleKey: 'successfullyProvisionedAppleWallet',
          messageKeys: ['checkAppleWalletForAddedEkey'],
          type: EMessageType.success,
          duration: 30000
        }));
        appInsights.trackEvent({ name: 'appleWalletProvisioning' }, 
          {
            message: `Apple Wallet Web Provisioning Success`,
            userId: currentUser?.id,
            organizationName: desiredOrgName,
            dealerName: desiredDealerName,
            dealerId: desiredDealerId,
          });

        // do after success function, or just refresh the wallet status to indicate the new values.
        dispatch(afterSuccess?.());
      } else {
        // console.log("u-aw: That didn't work.  Status: " + result.status);
        // console.log("u-aw: Result: " + JSON.stringify(result));
        dispatch(addAlert({
          id: Date.now(),
          titleKey: 'provisionAppleWalletPassesFailure',
          messageKeys: [result.statusMessage],
          type: EMessageType.error,
          duration: 20000
        }));
        appInsights.trackEvent({ name: 'appleWalletProvisioning' },
        {
          message: `Apple Wallet Web Provisioning Failure`,
          userId: currentUser?.id,
          username: currentUser?.username,
          location: window.location.href,
          organizationName: desiredOrgName,
          dealerName: desiredDealerName,
          dealerId: desiredDealerId,
          reason: `${result.status} ${result.statusMessage}`
        });
      }
    }
  }) as any;
};

interface IProps extends WrappedComponentProps, IPartnerCustomizations {
  match: any,
  history: any,
  darkModeActive: boolean,
  inviteToken?: string,
  desiredOrgName?: string,
  desiredDealerName?: string,
  appleWalletAllowedOverride?: boolean, // ditto ^
  afterSuccess?(): void,
}

const addToWalletButtonId = 'add-to-apple-wallet';

const AddToAppleWalletContainer: React.FC<IProps> = (props) => {
  const dispatch = useAppDispatch();

  const $currentUser: UserC = useAppSelector(state => state.authenticatedUser.currentUser);
  const $walletStatus: IAppleWalletPassStatusResponseC = useAppSelector(state => state.appleWalletStatus.walletStatus);
  const $loadingWalletStatus: boolean = useAppSelector(state => state.appleWalletStatus.loading);
  const $walletProvisioningAttempted: boolean = useAppSelector(state => state.appleWalletStatus.walletProvisioningAttempted);

  useEffect(() => {
    const inviteToken = props.inviteToken!;
    const decoded: any = decodeJwt(inviteToken);
    if (inviteToken && decoded) {
      dispatch(attemptCheckAppleWalletInvitationPassStatus({ inviteToken }))
        .then((walletStatusResponse: IAppleWalletPassStatusResponseC) => {
          
          // depending on the user's "is_sandbox" status, we should use either the
          // official wallet domain or the certification (sandbox) wallet endpoint
          var domainForAppleWallet = appleWalletDomain;
          if (walletStatusResponse.is_apple_sandbox_user) {
            domainForAppleWallet = appleWalletSandboxDomain;
          }
          
          // helper to start up the apple wallet script
          initAppleWalletScript(
            dispatch,
            domainForAppleWallet,
            inviteToken,
            decoded.dealer_id,
            decoded.dealer_name,
            decoded.org_name,
            $currentUser,
            props.afterSuccess
          );
        });

      }
      
  }, [props.inviteToken])

  useEffect(() => {
    const inviteToken = props.inviteToken!;

    if ($walletProvisioningAttempted) {
      // after provisioning continuously call status to see if it updates while viewing this component
        const recurringStatusCall = setInterval(() => {
          if (inviteToken) {
            dispatch(attemptSilentCheckAppleWalletInvitationPassStatus({ inviteToken }))
          }
        }, 5000);

        return () => {
        // unmount - clear the interval
        clearInterval(recurringStatusCall);
      }
    }
  }, [$walletProvisioningAttempted])

  if (props.render) {
    return props.render();
  }

  const appleWalletAllowed = true;
  // const appleWalletAllowed = props.appleWalletAllowedOverride || $siteData?.discoverableSettings?.get?.(EKnownSiteSettings.IsAppleWalletEnabled)?.value?.value;

  const userAppleWalletPhoneDeviceLimitReached = ($walletStatus?.active_phone_count ?? 0) >= ($walletStatus?.phone_limit ?? 1);
  const userAppleWalletPhoneMigrationLimitReached = ($walletStatus?.active_phone_count ?? 0) >= ($walletStatus?.phone_migration_limit ?? 1);
  const userAppleWalletWatchDeviceLimitReached = ($walletStatus?.active_watch_count ?? 0) >= ($walletStatus?.watch_limit ?? 1);
  const userAppleWalletWatchMigrationLimitReached = ($walletStatus?.active_watch_count ?? 0) >= ($walletStatus?.watch_migration_limit ?? 1);

  const deviceLimitReached = userAppleWalletPhoneDeviceLimitReached || userAppleWalletWatchDeviceLimitReached;
  const migrationLimitReached = userAppleWalletPhoneMigrationLimitReached || userAppleWalletWatchMigrationLimitReached;

  return (
    <UniConditionalRender visible={!!appleWalletAllowed}>

      {/* either show the loader or the button */}
      <UniConditionalRender visible={$loadingWalletStatus}>
        <Col xs={16} offset={{ xs: 4 }}>
          <div className="wallet-loader">
            <UniLoader type="radial" />
          </div>
        </Col>
      </UniConditionalRender>

      <UniConditionalRender visible={!$loadingWalletStatus}>
        
        {/* Messages for:
        *** - LT device migration limit
        *** - GTE device limit 
        */}
        <UniConditionalRender visible={deviceLimitReached && !migrationLimitReached}>
          <UniMessage
            hideIcon={true}
            type={EMessageType.warn}
            titleKey="appleDeviceLimitReached"
            messageKeys={[
              ...(userAppleWalletWatchDeviceLimitReached ? ['appleWatchDeviceLimitReached'] : []),
              ...(userAppleWalletPhoneDeviceLimitReached ? ['applePhoneDeviceLimitReached'] : []),
              '_appleDeviceLimitReachedSolution'
            ]} />
        </UniConditionalRender>

        {/* Messages for:
        *** - GTE device migration limit
        */}
        <UniConditionalRender visible={deviceLimitReached}>
          <UniMessage
            hideIcon={true}
            type={EMessageType.warn}
            titleKey="appleDeviceMigrationLimitReached"
            messageKeys={['_appleDeviceMigrationLimitReachedSolution']} />
        </UniConditionalRender>

      </UniConditionalRender>

      {/* Add-to-Wallet Button Display & Device Limit Counts */}
      {/* this img must be rendered initially otherwise the apple script will fail */}
      <div className="app-badge-link">

        <UniImg 
          id={addToWalletButtonId}
          className={classNames('app-badge-image apple-wallet', { 
            'not-clickable': migrationLimitReached, 
            'clickable-but-invisible': $loadingWalletStatus
          })}
          textKey="addToAppleWallet" 
          src="https://unikeystatic.blob.core.windows.net/assets/residential-app/shared/add-to-apple-wallet-badge.png" />
      
        <UniConditionalRender visible={!migrationLimitReached}>
          <div className={classNames('active-device-counts', { 'invisible': $loadingWalletStatus || (migrationLimitReached) })}>
            <span>
              <UniIcon 
                size="xs"
                name="infoOutline"
                tooltipTextKeys={['appleWalletDeviceLimits', 'appleWalletDeviceLimitCountsExplained[phones][maxPhones][watches][maxWatches]']}
                textKeyVariables={{
                  phones: `${$walletStatus?.active_phone_count ?? 0}`,
                  maxPhones: `${$walletStatus?.phone_limit ?? 0}`,
                  watches: `${$walletStatus?.active_watch_count ?? 0}`,
                  maxWatches: `${$walletStatus?.watch_limit ?? 0}`,
                }} />
            </span>
            <span>
              <UniLocalize 
                translate="appleWalletActive[phones][watches]deviceCounts"
                variables={{
                  phones: $walletStatus?.active_phone_count,
                  watches: $walletStatus?.active_watch_count
                }} />
            </span>
            
          </div>
        </UniConditionalRender>

      </div>
    </UniConditionalRender>
  )
}

export default PartnerCustomizations(injectIntl(AddToAppleWalletContainer), {
  componentName: 'AddToAppleWallet'
});